import type { Group } from '@medplum/fhirtypes';

import { THERAPY_SERVICE_TYPE_SYSTEM } from '@unify/fhir-model/constants';
import {
  clearGroupLifecycleInactiveTag,
  getActiveGroupPatientMemberCount,
  getGroupCapacity,
  getGroupDescription,
  getGroupEnrollmentLockDate,
  getGroupEnrollmentMode,
  getGroupFacilitators,
  getGroupLocation,
  getGroupRoom,
  getGroupDevices,
  getGroupModality,
  getGroupSchedule,
  getGroupTemplateRef,
  getManagingOrganizationId,
  isGroupEnrollmentLocked,
  isGroupLifecycleInactive,
  isTherapyCohortGroup,
  locationRef,
  organizationRef,
  practitionerRef,
  resolveGroupScheduleRecurrence,
  setGroupCapacity,
  setGroupDescription,
  setGroupEnrollmentLockDate,
  setGroupEnrollmentMode,
  setGroupFacilitators,
  setGroupLocation,
  setGroupRoom,
  setGroupDevices,
  setGroupModality,
  setGroupSchedule,
  setGroupTemplateRef,
  withTherapyCohortTag,
} from '@unify/fhir-model/resources/group';
import { normalizeEnrollmentMode } from './constants';
import {
  facilitatorReferencesFromForm,
  splitFacilitatorReferences,
  type GroupLifecycleStatus,
  type GroupSetupFormValues,
} from './types';

export function resolveGroupLifecycleStatus(input: {
  active: boolean;
  archived?: boolean;
}): GroupLifecycleStatus {
  return input.active ? 'active' : 'inactive';
}

export function groupResourceToFormValues(resource: Group): GroupSetupFormValues {
  const schedule = getGroupSchedule(resource);
  const capacity = getGroupCapacity(resource);
  const templateRef = getGroupTemplateRef(resource);
  const facilitators = getGroupFacilitators(resource);
  const storedEnrollmentMode = getGroupEnrollmentMode(resource);

  return {
    id: resource.id,
    active: resource.active === true,
    name: resource.name ?? '',
    // Photo is template-owned; joinGroupWithTemplate fills imageUrl for display.
    imageUrl: undefined,
    organizationId: getManagingOrganizationId(resource),
    organizationName: resource.managingEntity?.display,
    description: getGroupDescription(resource),
    templateReference: templateRef?.reference,
    templateDisplay: templateRef?.display,
    // Session HCS is template-owned; join fills programReference for activation UI.
    programReference: undefined,
    programDisplay: undefined,
    enrollmentMode: normalizeEnrollmentMode(storedEnrollmentMode),
    enrollmentModeStored: Boolean(storedEnrollmentMode),
    ...splitFacilitatorReferences(
      facilitators
        .map((r) => r.reference?.replace(/^Practitioner\//, ''))
        .filter((id): id is string => !!id),
    ),
    facilitatorDisplays: facilitators.map((r) => r.display ?? r.reference ?? ''),
    startDate: schedule.startDate ?? '',
    endDate: schedule.endDate,
    enrollmentLockDate: getGroupEnrollmentLockDate(resource),
    daysOfWeek: schedule.daysOfWeek,
    startTime: schedule.startTime ?? '',
    endTime: schedule.endTime ?? '',
    timezone: schedule.timezone,
    recurrence: resolveGroupScheduleRecurrence(schedule),
    sessionDates: schedule.sessionDates ?? [],
    sessionCount: schedule.sessionCount,
    minEnrollment: capacity.minEnrollment ?? 1,
    maxEnrollment: capacity.maxEnrollment ?? 1,
    modality: getGroupModality(resource),
    locationReference: getGroupLocation(resource)?.reference,
    locationDisplay: getGroupLocation(resource)?.display,
    roomId: getGroupRoom(resource)?.reference?.replace(/^Location\//, ''),
    roomDisplay: getGroupRoom(resource)?.display,
    deviceIds: getGroupDevices(resource)
      .map((r) => r.reference?.replace(/^Device\//, ''))
      .filter((id): id is string => Boolean(id)),
  };
}

export function formValuesToGroupResource(
  values: GroupSetupFormValues,
  existing?: Group,
): Group {
  const enrollmentMode = values.enrollmentMode;
  const recurrence = resolveGroupScheduleRecurrence({ recurrence: values.recurrence });
  const daysOfWeek =
    recurrence === 'weekly' || recurrence === 'biweekly' ? [...values.daysOfWeek] : [];
  const sessionCount = values.sessionCount;
  const endDate = values.endDate?.trim() || undefined;
  const enrollmentLockDate = values.enrollmentLockDate?.trim() || undefined;

  const base: Group = {
    resourceType: 'Group',
    id: existing?.id,
    meta: existing?.meta,
    type: 'person',
    actual: true,
    active: values.active === true,
    name: values.name.trim(),
    member: existing?.member,
    quantity: existing ? getActiveGroupPatientMemberCount(existing) : 0,
    code: {
      coding: [
        {
          system: THERAPY_SERVICE_TYPE_SYSTEM,
          code: 'group-therapy',
          display: 'Group Therapy',
        },
      ],
    },
    managingEntity: values.organizationId
      ? (organizationRef(values.organizationId, values.organizationName) as Group['managingEntity'])
      : existing?.managingEntity,
  };

  let resource = withTherapyCohortTag(base);
  // Lifecycle is Group.active only — strip legacy inactive tags on every save.
  resource = clearGroupLifecycleInactiveTag(resource);

  const facilitatorIds = facilitatorReferencesFromForm(values);
  resource = setGroupFacilitators(
    resource,
    facilitatorIds.map((id, idx) => practitionerRef(id, values.facilitatorDisplays?.[idx])),
  );
  resource = setGroupCapacity(resource, {
    minEnrollment: values.minEnrollment,
    maxEnrollment: values.maxEnrollment,
  });
  // Stage 1 may save before a schedule exists — keep prior schedule or omit.
  if (values.startDate?.trim()) {
    resource = setGroupSchedule(resource, {
      startDate: values.startDate,
      endDate,
      startTime: values.startTime,
      endTime: values.endTime,
      sessionCount,
      daysOfWeek,
      timezone: values.timezone,
      recurrence,
      sessionDates: recurrence === 'custom' ? values.sessionDates ?? [] : undefined,
    });
  } else if (existing) {
    const prior = getGroupSchedule(existing);
    if (prior.startDate) {
      resource = setGroupSchedule(resource, prior);
    }
  }
  resource = setGroupModality(resource, values.modality);
  resource = setGroupLocation(
    resource,
    values.locationReference
      ? locationRef(
          values.locationReference.replace(/^Location\//, ''),
          values.locationDisplay,
        )
      : undefined,
  );
  resource = setGroupRoom(
    resource,
    values.roomId
      ? locationRef(values.roomId.replace(/^Location\//, ''), values.roomDisplay)
      : undefined,
  );
  resource = setGroupDevices(
    resource,
    (values.deviceIds ?? []).map((id) => ({
      reference: id.startsWith('Device/') ? id : `Device/${id}`,
    })),
  );
  resource = setGroupEnrollmentMode(resource, enrollmentMode);
  resource = setGroupEnrollmentLockDate(
    resource,
    enrollmentMode === 'closed' ? enrollmentLockDate : undefined,
  );
  resource = setGroupDescription(resource, values.description);

  if (values.templateReference?.trim()) {
    resource = setGroupTemplateRef(resource, {
      reference: values.templateReference.trim().startsWith('HealthcareService/')
        ? values.templateReference.trim()
        : `HealthcareService/${values.templateReference.trim()}`,
      display: values.templateDisplay,
    });
  } else {
    resource = setGroupTemplateRef(resource, undefined);
  }

  // Intentionally NOT written (template-owned or retired):
  // session HCS, screening HCS, screening-required, IOP,
  // program UUID/name, image, previous-iteration.

  return resource;
}

export function groupListItemFromResource(resource: Group): GroupSetupFormValues & {
  enrolledCount: number;
  enrollmentLocked: boolean;
  lifecycleStatus: GroupLifecycleStatus;
} {
  const form = groupResourceToFormValues(resource);
  return {
    ...form,
    enrolledCount: getActiveGroupPatientMemberCount(resource),
    enrollmentLocked: isGroupEnrollmentLocked(resource),
    lifecycleStatus: resolveGroupLifecycleStatus({
      active: form.active,
      archived: isGroupLifecycleInactive(resource),
    }),
  };
}

export {
  getActiveGroupPatientMemberCount as getActiveGroupMemberCount,
  getGroupEnrollmentMode,
  isGroupEnrollmentLocked,
  isTherapyCohortGroup,
};
